import { baseApi } from './baseApi'
import { API_ENDPOINTS } from '@/constants'
import type {
  ApiResponse,
  FarmerProfile,
  UpdateFarmerProfileData,
  FarmerDashboardSummary,
  DetailedFarmStatistics,
  DashboardPreferences,
} from '@/types'

export const farmerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ===================================
    // Farmer Profile Queries
    // ===================================

    /**
     * Get farmer profile
     */
    getFarmerProfile: builder.query<FarmerProfile, void>({
      query: () => API_ENDPOINTS.FARMERS.PROFILE,
      transformResponse: (response: ApiResponse<FarmerProfile>) => response.data,
      providesTags: [{ type: 'FarmerProfile', id: 'CURRENT' }],
    }),

    /**
     * Get farmer dashboard summary
     */
    getFarmerDashboard: builder.query<FarmerDashboardSummary, void>({
      query: () => API_ENDPOINTS.FARMERS.DASHBOARD,
      transformResponse: (response: ApiResponse<FarmerDashboardSummary>) => response.data,
      providesTags: [{ type: 'FarmerDashboard', id: 'CURRENT' }],
    }),

    /**
     * Get detailed farm statistics (for a specific farm)
     */
    getDetailedFarmStatistics: builder.query<DetailedFarmStatistics, string>({
      query: (farmId) => `${API_ENDPOINTS.FARMERS.STATISTICS}?farmId=${farmId}`,
      transformResponse: (response: ApiResponse<DetailedFarmStatistics>) => response.data,
      providesTags: (_result, _error, farmId) => [
        { type: 'FarmStatistics', id: farmId },
      ],
    }),

    /**
     * Get dashboard preferences
     */
    getDashboardPreferences: builder.query<DashboardPreferences, void>({
      query: () => API_ENDPOINTS.FARMERS.DASHBOARD_PREFERENCES,
      transformResponse: (response: ApiResponse<DashboardPreferences>) => response.data,
      providesTags: [{ type: 'DashboardPreferences', id: 'CURRENT' }],
    }),

    // ===================================
    // Farmer Profile Mutations
    // ===================================

    /**
     * Update farmer profile
     */
    updateFarmerProfile: builder.mutation<FarmerProfile, UpdateFarmerProfileData>({
      query: (data) => ({
        url: API_ENDPOINTS.FARMERS.UPDATE_PROFILE,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<FarmerProfile>) => response.data,
      invalidatesTags: [
        { type: 'FarmerProfile', id: 'CURRENT' },
        { type: 'User', id: 'CURRENT' }, // Also invalidate user data
      ],
    }),

    /**
     * Update dashboard preferences
     */
    updateDashboardPreferences: builder.mutation<
      DashboardPreferences,
      Partial<DashboardPreferences>
    >({
      query: (data) => ({
        url: API_ENDPOINTS.FARMERS.DASHBOARD_PREFERENCES,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<DashboardPreferences>) => response.data,
      invalidatesTags: [{ type: 'DashboardPreferences', id: 'CURRENT' }],
    }),
  }),
})

export const {
  useGetFarmerProfileQuery,
  useGetFarmerDashboardQuery,
  useGetDetailedFarmStatisticsQuery,
  useGetDashboardPreferencesQuery,
  useUpdateFarmerProfileMutation,
  useUpdateDashboardPreferencesMutation,
} = farmerApi
